/**
 * The quick-capture bar: paste a link or type a name, then go save it.
 *
 * It moved here from `Home.tsx`, where it was the hero, because a second screen needed it — the places
 * list, which is where a user who has decided to add something is most likely to already be. Two copies
 * of "read the clipboard, then navigate with the text" would have been two chances for the paste button
 * to behave differently on the two screens, and the clipboard is exactly the kind of API where the
 * variants differ by browser rather than by screen.
 *
 * **One field, because the common save starts with a pasted link.** Measured in Phase 0 and written into
 * ADR-004: the user has the post open in another app, copies the link, and comes here. Asking for the URL
 * and then for a name doubles the work of the case that happens most, and the form on the other side
 * already derives a name from the link's handle when it can.
 *
 * **The paste button is deliberate**, not a shortcut around the platform's own. `navigator.clipboard`
 * needs a permission a page can be refused, the long-press paste menu differs on every platform, and on a
 * phone the whole gesture is one thumb-held menu over a field this control already renders. The button
 * makes the intent explicit and reports what happened in the one place the user is looking.
 *
 * It navigates rather than saving, and that is the design rather than an unfinished path: the capture
 * form is where a place is authored, it is the only screen that can do it (ADR-004), and the text travels
 * in the query string, which is the parameter that form reads. A save performed here would need every
 * field of that form to be optional a second time.
 */

import { useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { useNavigate } from "react-router-dom";

import { copy } from "../copy.ts";
import { asUrl } from "../lib/capture.ts";
import { searchCentre } from "../lib/placeSearch.ts";
import { usePlaceLookup } from "../lib/queries.ts";
import { useCurrentUser } from "../lib/session-context.tsx";
import { useDebouncedValue } from "../lib/useDebouncedValue.ts";
import { useToast } from "./Toast.tsx";

/**
 * How long the bar waits after a keystroke before asking the provider.
 *
 * The provider is a rate-limited upstream, not a local filter: every search is an autocomplete plus one
 * detail call per candidate, and the free tier allows 5 requests/second. Waiting for a pause is what
 * keeps a typed word from spending the ceiling — measured advice for that API as well as this app's own
 * arithmetic.
 */
const SUGGEST_DEBOUNCE_MS = 350;

export function CaptureBar(): ReactElement {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = useCurrentUser();
  const [text, setText] = useState("");
  /*
   * Whether the suggestion list belongs on screen.
   *
   * Gated on focus rather than left on after a pick: a dropdown that stayed open over the screen the
   * user was taken to would be a second copy of a control they have already finished with.
   */
  const [suggesting, setSuggesting] = useState(false);

  /*
   * The centre the search is ranked around.
   *
   * This bar has no pin and no trip of its own, so it uses the app's reference destination — the same
   * constant the map picker opens on. It is a ranking hint sent to the provider, never a coordinate that
   * gets saved: the place that is stored is the one the user confirms on the form.
   */
  const centre = searchCentre();
  const debounced = useDebouncedValue(text.trim(), SUGGEST_DEBOUNCE_MS);
  // A pasted link is the capture form's input, not a name to search for: the form reads it locally or
  // asks `/places/map-link`, and searching the provider with a URL would find nothing and say so.
  const lookup = usePlaceLookup(
    user?.id ?? "",
    debounced,
    centre,
    suggesting && user !== null && asUrl(text) === null,
  );
  const suggestions = lookup.data?.results ?? [];
  const showList = suggesting && asUrl(text) === null && debounced.length >= 2;

  /** Take a suggestion to the form, carrying the provider's own fields. */
  function chooseSuggestion(result: (typeof suggestions)[number]): void {
    const params = new URLSearchParams({ name: result.name });
    if (result.address !== null) params.set("address", result.address);
    // Only when the provider actually positioned it: the contract allows `location: null`, and writing
    // "null" into the URL would be a coordinate the capture form then has to refuse.
    if (result.location !== null) {
      params.set("lat", String(result.location.lat));
      params.set("lon", String(result.location.lon));
    }
    params.set("provider", result.provider);
    if (result.provider_place_id !== null) params.set("provider_place_id", result.provider_place_id);
    // The provider's own category, when it named one the contract knows, so the form's chips are
    // filled from what was already answered.
    const category = result.categories[0];
    if (category !== undefined) params.set("category", category);
    setSuggesting(false);
    navigate(`/capture?${params.toString()}`);
  }

  /**
   * Read the clipboard into the field, and say what happened either way.
   *
   * Every failure branch reports: an empty clipboard, a refused permission and a browser without the API
   * are three different facts, and a button that silently does nothing for all three is a button the user
   * presses twice and then stops trusting.
   */
  async function pasteFromClipboard(): Promise<void> {
    try {
      // Checked before the read, not after: a browser without the API throws on property access, and the
      // catch below would then report it as a refused permission — a different and wrong explanation.
      if (!navigator.clipboard?.readText) {
        showToast(copy.captureBar.pasteUnsupported);
        return;
      }
      const text = await navigator.clipboard.readText();
      if (text.trim() !== "") {
        setText(text.trim());
        showToast(copy.captureBar.pasted);
      } else {
        showToast(copy.captureBar.pasteEmpty);
      }
    } catch {
      // The permission prompt was dismissed, or the page is not a secure context.
      showToast(copy.captureBar.pasteDenied);
    }
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    const trimmed = text.trim();
    // An empty submit does nothing rather than opening a blank form: the field is the whole control, and
    // a save with nothing in it is a form the user did not ask for. The manual path is a separate chip.
    if (trimmed === "") return;
    navigate(`/capture?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <form
      className="capturebar"
      onSubmit={onSubmit}
      /*
       * Closes the list when focus leaves the whole control, and not on the input's own blur: a blur
       * fires before a click on a row lands, so closing there would unmount the row being pressed. The
       * `relatedTarget` check asks the form — which contains both — whether the focus stayed inside.
       */
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setSuggesting(false);
      }}
    >
      <div className="capturebar__wrap">
        <span className="capturebar__icon material-symbols-outlined" aria-hidden="true">
          search
        </span>
        <input
          className="capturebar__input"
          type="text"
          /*
           * A label and not only a placeholder. `placeholder` disappears the moment anything is typed, and
           * a screen reader with an empty field has nothing else to read — the placeholder is a hint, and
           * the accessible name has to survive the first keystroke.
           */
          aria-label={copy.captureBar.label}
          placeholder={copy.captureBar.placeholder}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onFocus={() => setSuggesting(true)}
          onKeyDown={(event) => {
            // Escape is the standard way out of a list, and it must not submit the form.
            if (event.key === "Escape") setSuggesting(false);
          }}
          autoCapitalize="none"
          autoCorrect="off"
        />
        <button
          type="button"
          className="capturebar__paste"
          title={copy.captureBar.pasteTitle}
          onClick={() => void pasteFromClipboard()}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            content_paste
          </span>
          <span>{copy.captureBar.paste}</span>
        </button>

        {showList && (
          <div className="capturebar__suggest" aria-label={copy.captureBar.suggestionsLabel}>
            {lookup.isError ? (
              // The provider could not be asked. The form is still reachable — a link, or a name typed
              // and saved without a coordinate — so the sentence says what still works.
              <p className="field__help">{copy.capture.providerUnavailable}</p>
            ) : lookup.isPending ? (
              <p className="field__help">{copy.capture.searching}</p>
            ) : (
              <ul className="cards">
                <li className="card card--row">
                  <div className="card__link">
                    <span className="card__title">✨ {debounced}</span>
                    <span className="card__meta">{copy.captureBar.aiSuggestionMeta}</span>
                  </div>
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      setSuggesting(false);
                      navigate(`/capture?q=${encodeURIComponent(debounced)}&ai=1`);
                    }}
                  >
                    {copy.captureBar.aiAction}
                  </button>
                </li>
                {suggestions.length === 0 ? (
                  <li className="field__help">
                    {copy.capture.providerNoResults}
                  </li>
                ) : (
                  suggestions.map((result) => (
                    <li key={`${result.provider}-${result.provider_place_id ?? result.name}`} className="card card--row">
                      <div className="card__link">
                        <span className="card__title">{result.name}</span>
                        {result.address !== null && <span className="card__meta">{result.address}</span>}
                        {/* Which index answered, named — the two have different coverage. */}
                        <span className="card__meta">
                          {copy.capture.providerSourceLabel}{" "}
                          {copy.capture.providerLabels[result.provider] ?? copy.capture.providerLabelFallback}
                        </span>
                      </div>
                      <button type="button" className="link" onClick={() => chooseSuggestion(result)}>
                        {copy.captureBar.useSuggestion}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}
      </div>
      <button type="submit" className="button button--primary capturebar__submit">
        <span>{copy.captureBar.action}</span>
        <span className="material-symbols-outlined" aria-hidden="true">
          bookmark_add
        </span>
      </button>
    </form>
  );
}
